import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import styled from 'styled-components'

import { CartPlugin } from '@api/local/CartPlugin'
import { Drawer, SectionLoading } from '@client/components/molecules'
import { useConfig } from '@client/contexts/ConfigProvider'
import { CheckOutFlowTypeEnum, useUserCartQuery } from '@hooks/api'
import * as checkoutStorage from '@utility/CheckoutStorage'

import { CartBreakdown, CartFooter, CartHeader, CartWallet, CartAddOns, CartMealKit, CartDiscount } from '../cart'

const Container = styled.div`
  height: 100%;
  width: 100%;
  z-index: 8;
`

export enum CartTabs {
  MEAL_KIT = 'meal-kit',
  ADD_ONS = 'add-ons'
}

interface CartDrawerState {
  showTabBar: boolean
  tab: CartTabs
  numberOfAddons: number
}

const DEFAULT_STATE: CartDrawerState = {
  showTabBar: false,
  tab: CartTabs.MEAL_KIT,
  numberOfAddons: null,
}

export function CartDrawer(): JSX.Element {

  const config = useConfig()
  const { data: cartData, loading: cartLoading } = useUserCartQuery({ ssr: config.fetchSSRQuery() })

  const [state, setState] = useState<CartDrawerState>({ ...DEFAULT_STATE })
  const menu = cartData?.currentUser?.activeMenu
  const cartStatus = cartData?.currentUser?.checkoutStatus
  const hasMealKits = cartStatus?.hasConfiguredBox
  const hasAddOns = cartStatus?.hasOnDemandProducts
  const numberOfAddons = cartData?.currentUser?.activeCart?.totalNonSubscriptionItems
  const isDrawerOpen = cartData?.currentUser?.activeCart?.open
  const isSubscriptionFlow = checkoutStorage.getCheckoutSession('checkoutFlowType') === CheckOutFlowTypeEnum.SUBSCRIPTION

  const _handleClose = (): void => {
    CartPlugin.shared().close()
  }

  const _handleOnTabChange = (tab: CartTabs): void => {
    setState((prevState) => update(prevState, {
      tab: { $set: tab },
    }))
  }

  const $header = <CartHeader
    tab={state.tab}
    deliveryDate={menu?.deliveryDate}
    billingDate={menu?.billingDate}
    showTabBar={state.showTabBar}
    onClose={_handleClose}
    onTabChange={_handleOnTabChange} />

  const $footer = <CartFooter />

  useEffect(() => {
    if (!cartLoading) {
      const showTabBar = hasAddOns ? cartStatus?.hasActiveSubscription || hasMealKits : false
      let addonsHasChanged = false
      if (numberOfAddons !== state.numberOfAddons && state.numberOfAddons) {
        addonsHasChanged = true
      }

      let tab = (isSubscriptionFlow || hasMealKits || cartStatus?.hasActiveSubscription) && (!addonsHasChanged || (addonsHasChanged && (!isDrawerOpen || !numberOfAddons))) ? CartTabs.MEAL_KIT : CartTabs.ADD_ONS

      if (tab === CartTabs.ADD_ONS && state.tab === CartTabs.ADD_ONS && hasAddOns) {
        tab = CartTabs.ADD_ONS
      }
      setState((prevState) => update(prevState, {
        tab: { $set: tab },
        showTabBar: { $set: showTabBar },
        numberOfAddons: { $set: numberOfAddons },
      }))
    }

  }, [cartData?.currentUser?.activeCart?.totalItems, cartStatus?.hasActiveSubscription])

  return (
    <Drawer
      open={isDrawerOpen}
      allowClose={!cartLoading}
      header={$header}
      footer={$footer}>
      <Container>
        <Choose>
          <When condition={cartLoading}>
            <SectionLoading />
          </When>
          <When condition={state.tab === CartTabs.MEAL_KIT}>
            <CartMealKit />
          </When>
          <Otherwise>
            <CartAddOns />
          </Otherwise>
        </Choose>
        <CartWallet />
        <CartBreakdown />
        <CartDiscount />
      </Container>
    </Drawer>
  )
}
